import React from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { BulkRegisterRow, BulkRegisterRowStatus, CsvFieldDefinition, CsvParsedRow } from './types';

type CsvPreviewTableProps = {
  /** 表示する行データ（バリデーション済み or 登録処理中） */
  rows: (CsvParsedRow | BulkRegisterRow)[];
  /** フィールド定義 */
  fields: CsvFieldDefinition[];
  /** コード値 → 表示値 変換関数 */
  resolveDisplayValue: (key: string, rawValue: string) => string | null;
  /** 登録処理中かどうか（trueの場合はregisterStatusを使用） */
  isRegistering?: boolean;
};

/** ステータスアイコンを返す */
const StatusIcon: React.FC<{ row: CsvParsedRow | BulkRegisterRow; isRegistering: boolean }> = ({
  row,
  isRegistering,
}) => {
  if (isRegistering && 'registerStatus' in row) {
    const bulkRow = row as BulkRegisterRow;
    return <RegisterStatusIcon status={bulkRow.registerStatus} error={bulkRow.registerError} />;
  }

  if (row.status === 'success') {
    return (
      <Tooltip title="読み込み成功">
        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={row.errorMessages.join('\n')}>
      <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
    </Tooltip>
  );
};

/** 登録処理ステータスアイコン */
const RegisterStatusIcon: React.FC<{ status: BulkRegisterRowStatus; error?: string }> = ({
  status,
  error,
}) => {
  switch (status) {
    case 'pending':
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    case 'processing':
      return <CircularProgress size={18} />;
    case 'success':
      return (
        <Tooltip title="登録成功">
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
        </Tooltip>
      );
    case 'error':
      return (
        <Tooltip title={error || '登録失敗'}>
          <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
        </Tooltip>
      );
  }
};

/**
 * CSV一括登録プレビューテーブル
 */
const CsvPreviewTable: React.FC<CsvPreviewTableProps> = ({
  rows,
  fields,
  resolveDisplayValue,
  isRegistering = false,
}) => {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        maxHeight: '50vh',
        overflow: 'auto',
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 600,
                position: 'sticky',
                left: 0,
                zIndex: 3,
                bgcolor: 'background.paper',
                width: 40,
                minWidth: 40,
              }}
            />
            {fields.map((field) => (
              <TableCell key={field.key} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {field.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.index}>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  borderRight: 1,
                  borderRightColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StatusIcon row={row} isRegistering={isRegistering} />
                </Box>
              </TableCell>
              {fields.map((field) => {
                const rawValue = row.data[field.key] ?? '';
                const isError = row.errorFields.includes(field.key);
                const displayValue = resolveDisplayValue(field.key, rawValue);

                return (
                  <TableCell
                    key={field.key}
                    sx={{
                      whiteSpace: 'nowrap',
                      bgcolor: isError ? 'error.main' : undefined,
                      color: isError ? 'error.contrastText' : undefined,
                    }}
                  >
                    <Typography variant="body2">{displayValue ?? (rawValue || '—')}</Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CsvPreviewTable;
